import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, Suspense, lazy } from 'react';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, ProtectedRoute } from './components/auth';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { PublicLayout } from './components/layout/PublicLayout';
import { ScreenReaderAnnouncer } from './lib/accessibility';
import { useAuthStore } from './store/auth-store';
import {
  CalendarPageLoader,
  BookingsPageLoader,
  PublicProfilePageLoader,
  ErrorBoundary,
} from './components/common';
import DesignSystem from './pages/DesignSystem';
import LayoutTest from './pages/LayoutTest';
import Register from './pages/auth/Register';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import VerifyEmailSuccess from './pages/auth/VerifyEmailSuccess';
import { OnboardingDemo } from './pages/OnboardingDemo';
import { CheckoutSuccess } from './pages/dashboard/CheckoutSuccess';
import { PersonalInfo } from './pages/onboarding/PersonalInfo';
import { Terms } from './pages/onboarding/Terms';
import NotFound from './pages/errors/NotFound';
import ServerError from './pages/errors/ServerError';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import TermsAndConditions from './pages/legal/TermsAndConditions';
import { isPersonalInfoComplete } from './lib/onboarding-completion';
import { hasActiveSubscription } from './lib/subscription';

// Lazy load dashboard pages for code splitting
const MyCalendar = lazy(() => import('./pages/dashboard/MyCalendar'));
const Bookings = lazy(() => import('./pages/dashboard/Bookings'));
const MyClients = lazy(() => import('./pages/dashboard/MyClients'));
const EngagementLibrary = lazy(() => import('./pages/dashboard/EngagementLibrary'));
const Course = lazy(() => import('./pages/dashboard/Course'));
const CaregiverConfidence = lazy(() => import('./pages/dashboard/CaregiverConfidence'));
const CallyCoach = lazy(() => import('./pages/dashboard/CallyCoach'));
const ManagePlan = lazy(() => import('./pages/dashboard/ManagePlan'));
const ProfileSettings = lazy(() => import('./pages/dashboard/ProfileSettings'));
const DailyCounts = lazy(() => import('./pages/dashboard/DailyCounts'));
const SupervisedCaregivers = lazy(() => import('./pages/dashboard/SupervisedCaregivers'));
const SupervisedCaregiverClients = lazy(
  () => import('./pages/dashboard/SupervisedCaregiverClients')
);
const SupervisedCaregiverBookings = lazy(
  () => import('./pages/dashboard/SupervisedCaregiverBookings')
);
// const ShiftNotes = lazy(() =>
//   import('./pages/dashboard/ShiftNotes').then(module => ({ default: module.ShiftNotes }))
// );

// Lazy load public profile page
const CaregiverProfile = lazy(() => import('./pages/public/CaregiverProfile'));
const ClientAuthentication = lazy(() => import('./pages/public/ClientAuthentication'));
const BookingDetails = lazy(() => import('./pages/public/BookingDetails'));

/**
 * Auth redirect component that prevents authenticated users from accessing auth pages
 */
function AuthRedirect({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isProfileComplete, user } = useAuthStore();

  // If authenticated, redirect based on profile status
  if (isAuthenticated()) {
    // TODO: Re-enable email verification when backend email sending is configured
    // if (user && !user.is_verified) {
    //   return <Navigate to="/auth/verify-email" replace />
    // }

    if (!isProfileComplete) {
      const redirectTo = isPersonalInfoComplete(user)
        ? '/onboarding/terms'
        : '/onboarding/personal-info';
      return <Navigate to={redirectTo} replace />;
    }

    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function DashboardIndexRedirect() {
  const user = useAuthStore(state => state.user);
  const isAgencyCaregiver =
    user?.userRole === 'CARE_GIVER' && user.supervisorContext?.supervisorType === 'AGENCY';
  const redirectTo =
    user?.userRole === 'SUPERVISOR'
      ? hasActiveSubscription(user.subscription)
        ? '/dashboard/caregivers'
        : '/dashboard/manage-plan'
      : isAgencyCaregiver
        ? '/dashboard/engagement-library'
        : '/dashboard/calendar';

  return <Navigate to={redirectTo} replace />;
}

function App() {
  useEffect(() => {
    // Initialize screen reader announcer
    ScreenReaderAnnouncer.getInstance();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Skip link for keyboard navigation */}
        <a
          href="#main-content"
          className="skip-link"
          onFocus={e => (e.currentTarget.style.top = '6px')}
          onBlur={e => (e.currentTarget.style.top = '-40px')}
        >
          Skip to main content
        </a>

        <div id="app" role="application" aria-label="CareCal - Caregiver Scheduling Platform">
          <Routes>
            {/* Root route redirects to the dashboard if authenticated, otherwise to the login page */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Public legal pages */}
            <Route element={<PublicLayout />}>
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            </Route>

            {/* Auth routes - redirect if already authenticated */}
            <Route
              path="/b2b"
              element={
                <AuthRedirect>
                  <Register mode="agency" />
                </AuthRedirect>
              }
            />
            <Route
              path="/auth/register"
              element={
                <AuthRedirect>
                  <Register />
                </AuthRedirect>
              }
            />
            <Route
              path="/auth/login"
              element={
                <AuthRedirect>
                  <Login />
                </AuthRedirect>
              }
            />

            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />

            {/* Email verification routes - protected but allow unverified users */}
            <Route path="/auth/verify-email" element={<VerifyEmail />} />
            <Route
              path="/auth/verify-email-success"
              element={
                <ProtectedRoute allowUnverified={true}>
                  <VerifyEmailSuccess />
                </ProtectedRoute>
              }
            />

            {/* Public booking link - no auth required */}
            <Route
              path="/book/:linkCode"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<PublicProfilePageLoader />}>
                    <CaregiverProfile />
                  </Suspense>
                </ErrorBoundary>
              }
            />
            <Route path="/profile/:linkCode">
              <Route
                index
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<PublicProfilePageLoader />}>
                      <CaregiverProfile />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route path="verify">
                <Route
                  index
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<PublicProfilePageLoader />}>
                        <ClientAuthentication />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="code"
                  element={
                    <ErrorBoundary>
                      <Suspense fallback={<PublicProfilePageLoader />}>
                        <ClientAuthentication />
                      </Suspense>
                    </ErrorBoundary>
                  }
                />
              </Route>
            </Route>
            <Route
              path="/view/booking/:locator"
              element={
                <ErrorBoundary>
                  <Suspense fallback={<PublicProfilePageLoader />}>
                    <BookingDetails />
                  </Suspense>
                </ErrorBoundary>
              }
            />

            {/* Onboarding routes - protected but don't require profile completion */}
            <Route
              path="/onboarding/personal-info"
              element={
                <ProtectedRoute requireEmailVerified={true}>
                  <PersonalInfo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/terms"
              element={
                <ProtectedRoute requireEmailVerified={true}>
                  <Terms />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/manage-plan/success"
              element={
                <ProtectedRoute requireProfileComplete={true}>
                  <CheckoutSuccess />
                </ProtectedRoute>
              }
            />

            {/* Dashboard routes - require complete profile */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireProfileComplete={true}>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardIndexRedirect />} />
              <Route
                path="caregivers"
                element={
                  <ProtectedRoute requireActiveSubscription={true}>
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center py-12">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                          </div>
                        }
                      >
                        <SupervisedCaregivers />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="teens/:caregiverId/clients"
                element={
                  <ErrorBoundary>
                    <Suspense
                      fallback={
                        <div className="flex items-center justify-center py-12">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                        </div>
                      }
                    >
                      <SupervisedCaregiverClients />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="teens/:caregiverId/bookings"
                element={
                  <ErrorBoundary>
                    <Suspense
                      fallback={
                        <div className="flex items-center justify-center py-12">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                        </div>
                      }
                    >
                      <SupervisedCaregiverBookings />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="calendar"
                element={
                  <ProtectedRoute requireActiveSubscription={true}>
                    <ErrorBoundary>
                      <Suspense fallback={<CalendarPageLoader />}>
                        <MyCalendar />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="bookings"
                element={
                  <ProtectedRoute requireActiveSubscription={true}>
                    <ErrorBoundary>
                      <Suspense fallback={<BookingsPageLoader />}>
                        <Bookings />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="clients"
                element={
                  <ProtectedRoute requireActiveSubscription={true}>
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center py-12">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                          </div>
                        }
                      >
                        <MyClients />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="engagement-library"
                element={
                  <ProtectedRoute requireActiveSubscription={true}>
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center py-12">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                          </div>
                        }
                      >
                        <EngagementLibrary />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="course"
                element={
                  <ProtectedRoute requireActiveSubscription={true}>
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center py-12">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                          </div>
                        }
                      >
                        <Course />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="caregiver-confidence"
                element={
                  <ProtectedRoute requireActiveSubscription={true}>
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center py-12">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                          </div>
                        }
                      >
                        <CaregiverConfidence />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="cally-coach"
                element={
                  <ProtectedRoute requireActiveSubscription={true}>
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center py-12">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                          </div>
                        }
                      >
                        <CallyCoach />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              {/* <Route
                path="shift-notes"
                element={
                  <ProtectedRoute requireActiveSubscription={true}>
                    <ErrorBoundary>
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center py-12">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                          </div>
                        }
                      >
                        <ShiftNotes />
                      </Suspense>
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              /> */}
              <Route
                path="daily-counts"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<p role="status">Loading daily counts...</p>}>
                      <DailyCounts />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="settings"
                element={
                  <ErrorBoundary>
                    <Suspense
                      fallback={
                        <div className="flex items-center justify-center py-12">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                        </div>
                      }
                    >
                      <ProfileSettings />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="manage-plan"
                element={
                  <ErrorBoundary>
                    <Suspense
                      fallback={
                        <div className="flex items-center justify-center py-12">
                          <div className="h-8 w-8 animate-spin rounded-full border-4 border-lilac-500 border-t-blueviolet" />
                        </div>
                      }
                    >
                      <ManagePlan />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
            </Route>

            {/* Development/testing routes */}
            <Route path="/onboarding-demo" element={<OnboardingDemo />} />
            <Route path="/design-system" element={<DesignSystem />} />
            <Route path="/layout-test/*" element={<LayoutTest />} />

            {/* Error routes */}
            <Route path="/404" element={<NotFound />} />
            <Route path="/500" element={<ServerError />} />

            {/* Catch-all route for 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>

          <Toaster />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
